function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import ServiceFactory from "../service";

/*
 * Manual add/edit market drawer (Manage Data → Markets).
 * Mockup: PlunkDocs specs/mockups/manage-data-manual-edit.html §1.
 *
 * One form for add + edit. Type is DERIVED, never picked: an address makes
 * it a Store, geo-targets alone make it an Area (mirrors the importer rule).
 * Reference ID auto-slugs from Name and stays editable until first save,
 * locked after (it keys campaign/insights joins).
 *
 * API (LocationService, built 2026-08-02):
 *   POST /v1/location          add    (CreateLocation fields, camelCase)
 *   PUT  /v1/location/{id}     edit   (geo-target rows replaced; referenceId locked)
 *   DELETE /v1/location/{id}   delete (idempotent, removes geo targets)
 * geoTargetPostalCodes is a comma-joined string. Ids exceed 2^53 — they
 * arrive as JSONbig opaques; only ever String(id) them into URLs.
 */

const slug = s => String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
const inp = {
  width: "100%",
  boxSizing: "border-box",
  height: "38px",
  padding: "0 10px",
  marginTop: "5px",
  display: "block",
  border: "1px solid var(--plk-border, #d5dbe3)",
  borderRadius: "8px",
  fontSize: "13.5px",
  background: "#fff"
};
const lbl = {
  fontSize: "12.5px",
  fontWeight: 600,
  color: "#3c4048",
  display: "block",
  marginBottom: "12px"
};
const row2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px"
};
const row3 = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr",
  gap: "10px"
};
export default class MarketDrawer extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "field", k => e => {
      const v = e.target.value;
      const patch = {
        [k]: v,
        error: ""
      };
      // Auto-slug the reference id from the name until the user touches it
      // (add mode only — locked after first save).
      if (k === "name" && !this.isEdit() && !this.state.refTouched) patch.referenceId = slug(v);
      if (k === "referenceId") patch.refTouched = true;
      this.setState(patch);
    });
    _defineProperty(this, "addPostal", () => {
      const raw = this.state.postalInput;
      const parts = String(raw).split(/[\s,;]+/).map(p => p.trim()).filter(Boolean);
      if (!parts.length) return;
      const bad = parts.find(p => !/^[a-zA-Z0-9-]{3,10}$/.test(p));
      if (bad) {
        this.setState({
          error: `“${bad}” doesn't look like a postal code.`
        });
        return;
      }
      const postals = [...this.state.postals];
      parts.forEach(p => {
        if (!postals.includes(p)) postals.push(p);
      });
      this.setState({
        postals,
        postalInput: "",
        error: ""
      });
    });
    _defineProperty(this, "removePostal", p => this.setState({
      postals: this.state.postals.filter(x => x !== p)
    }));
    _defineProperty(this, "save", async () => {
      const s = this.state;
      if (!s.name.trim()) {
        this.setState({
          error: "Name is required."
        });
        return;
      }
      if (s.region && !/^[a-zA-Z]{2}$/.test(s.region.trim())) {
        this.setState({
          error: "Region should be the 2-letter state/province code."
        });
        return;
      }
      this.setState({
        saving: true,
        error: ""
      });
      const body = {
        name: s.name.trim(),
        referenceId: s.referenceId.trim() || null,
        streetAddress: s.streetAddress.trim() || null,
        streetAddress2: s.streetAddress2.trim() || null,
        city: s.city.trim() || null,
        region: s.region.trim().toLowerCase() || null,
        postalCode: s.postalCode.trim() || null,
        country: s.country.trim() || null,
        websiteUrl: s.websiteUrl.trim() || null,
        emailAddress: s.emailAddress.trim() || null,
        phoneNumber: s.phoneNumber.trim() || null,
        impressionWeight: Number(s.impressionWeight) || 0,
        geoTargetPostalCodes: s.postals.join(",") || null
      };
      try {
        const api = this.isEdit() ? `/location/${String(this.props.market.id)}` : "/location";
        const Service = ServiceFactory.getType(api, "location");
        const svc = new Service();
        if (this.isEdit()) await svc.putAsync(body);else await svc.postAsync(body);
        this.props.onSaved();
      } catch (err) {
        this.setState({
          saving: false,
          error: err.message || "Save failed."
        });
      }
    });
    _defineProperty(this, "remove", async () => {
      this.setState({
        saving: true,
        error: ""
      });
      try {
        const api = `/location/${String(this.props.market.id)}`;
        const Service = ServiceFactory.getType(api, "location");
        await new Service().deleteAsync();
        this.props.onSaved();
      } catch (err) {
        this.setState({
          saving: false,
          confirmDelete: false,
          error: err.message || "Delete failed."
        });
      }
    });
    const m = props.market;
    this.state = {
      name: m ? m.name || "" : "",
      referenceId: m ? m.referenceId || "" : "",
      refTouched: !!m,
      streetAddress: m ? m.streetAddress || "" : "",
      streetAddress2: m ? m.streetAddress2 || "" : "",
      city: m ? m.city || "" : "",
      region: m ? m.region || "" : "",
      postalCode: m ? m.postalCode || "" : "",
      country: m ? m.country || "" : "",
      websiteUrl: m ? m.websiteUrl || "" : "",
      emailAddress: m ? m.emailAddress || "" : "",
      phoneNumber: m ? m.phoneNumber || "" : "",
      impressionWeight: m ? String(m.impressionWeight || "") : "",
      postals: m ? (m.locationGeoTargets || []).filter(g => String(g.type).toLowerCase() === "postalcode").map(g => g.value) : [],
      postalInput: "",
      saving: false,
      confirmDelete: false,
      error: ""
    };
  }
  isEdit() {
    return !!this.props.market;
  }
  derivedType() {
    const hasAddress = this.state.streetAddress.trim() && this.state.city.trim();
    if (hasAddress) return "Store";
    if (this.state.postals.length) return "Area";
    return null;
  }
  render() {
    const s = this.state;
    const edit = this.isEdit();
    const type = this.derivedType();
    const overlay = {
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,35,.45)",
      zIndex: 1000,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      overflowY: "auto",
      padding: "40px 16px"
    };
    return /*#__PURE__*/React.createElement("div", {
      style: overlay,
      onClick: e => {
        if (e.target === e.currentTarget && !s.saving) this.props.onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        background: "#fff",
        borderRadius: "12px",
        width: "600px",
        maxWidth: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        boxSizing: "border-box",
        padding: "22px 24px",
        boxShadow: "0 12px 40px rgba(0,0,0,.25)"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "14px"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: "18px",
        fontWeight: 700,
        color: "#232020"
      }
    }, edit ? `Edit ${this.props.market.name}` : "Add a market"), /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      style: {
        cursor: "pointer",
        color: "#9aa4b2"
      },
      onClick: () => !s.saving && this.props.onClose()
    }, "close")), /*#__PURE__*/React.createElement("div", {
      style: row2
    }, /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "Name *", /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.name,
      onChange: this.field("name"),
      autoFocus: !edit
    })), /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "Reference ID", /*#__PURE__*/React.createElement("input", {
      style: {
        ...inp,
        background: edit ? "#f2f4f7" : "#fff"
      },
      value: s.referenceId,
      onChange: this.field("referenceId"),
      disabled: edit,
      title: edit ? "Can't be changed after the market is saved, because ads and reports refer to it" : "Made from the name; you can change it until you save"
    }))), type && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 10px"
      }
    }, type === "Store" ? /*#__PURE__*/React.createElement(React.Fragment, null, "This is a ", /*#__PURE__*/React.createElement("b", null, "store"), " because it has an address. Remove the address and keep ZIP codes to make it an area.") : /*#__PURE__*/React.createElement(React.Fragment, null, "This is an ", /*#__PURE__*/React.createElement("b", null, "area"), " because it has ZIP codes and no address. Add a street address and city to make it a store.")), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#232020",
        margin: "14px 0 8px"
      }
    }, "Address"), /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "Street", /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.streetAddress,
      onChange: this.field("streetAddress")
    })), /*#__PURE__*/React.createElement("div", {
      style: row3
    }, /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "City", /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.city,
      onChange: this.field("city")
    })), /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "Region", /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.region,
      onChange: this.field("region"),
      placeholder: "State",
      maxLength: 2
    })), /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "ZIP code", /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.postalCode,
      onChange: this.field("postalCode")
    }))), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#232020",
        margin: "14px 0 8px"
      }
    }, "Contact ", /*#__PURE__*/React.createElement("span", {
      style: {
        fontWeight: 400,
        color: "#5b616b"
      }
    }, "(optional)")), /*#__PURE__*/React.createElement("div", {
      style: row2
    }, /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "Website", /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.websiteUrl,
      onChange: this.field("websiteUrl"),
      placeholder: "https://\u2026"
    })), /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "Phone", /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.phoneNumber,
      onChange: this.field("phoneNumber")
    }))), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#232020",
        margin: "14px 0 8px"
      }
    }, "Area it covers"), /*#__PURE__*/React.createElement("div", {
      style: row2
    }, /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "Ad weight", /*#__PURE__*/React.createElement("input", {
      style: inp,
      type: "number",
      min: "0",
      value: s.impressionWeight,
      onChange: this.field("impressionWeight")
    })), /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, "ZIP codes it covers", /*#__PURE__*/React.createElement("span", {
      style: {
        display: "flex",
        gap: "6px",
        alignItems: "stretch"
      }
    }, /*#__PURE__*/React.createElement("input", {
      style: inp,
      value: s.postalInput,
      onChange: e => this.setState({
        postalInput: e.target.value,
        error: ""
      }),
      onKeyDown: e => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.addPostal();
        }
      },
      placeholder: "10001, 10011\u2026"
    }), /*#__PURE__*/React.createElement(Button, {
      onClick: this.addPostal,
      style: {
        height: "38px",
        marginTop: "5px",
        flex: "none"
      }
    }, "Add")))), s.postals.length > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        marginBottom: "12px"
      }
    }, (s.showAllZips ? s.postals : s.postals.slice(0, 12)).map(p => /*#__PURE__*/React.createElement("span", {
      key: p,
      style: {
        background: "#eef2f7",
        borderRadius: "12px",
        padding: "3px 10px",
        fontSize: "12px",
        display: "inline-flex",
        alignItems: "center",
        gap: "5px"
      }
    }, p, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      style: {
        fontSize: "14px",
        cursor: "pointer",
        color: "#9aa4b2"
      },
      onClick: () => this.removePostal(p)
    }, "close"))), s.postals.length > 12 && /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: () => this.setState({
        showAllZips: !s.showAllZips
      }),
      style: {
        border: 0,
        background: "transparent",
        color: "#c2382e",
        fontSize: "12.5px",
        fontWeight: 600,
        cursor: "pointer",
        padding: "3px 4px"
      }
    }, s.showAllZips ? "Show fewer" : `Show all ${s.postals.length}`)), s.postals.length > 0 && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "-4px 0 10px"
      }
    }, s.postals.length, " ZIP code", s.postals.length === 1 ? "" : "s", " in this area."), s.error && /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#c0392b",
        fontSize: "12px",
        margin: "4px 0 8px"
      }
    }, s.error), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "14px"
      }
    }, /*#__PURE__*/React.createElement("span", null, edit && !s.confirmDelete && /*#__PURE__*/React.createElement("a", {
      style: {
        color: "#c0392b",
        fontSize: "12px",
        cursor: "pointer"
      },
      onClick: () => this.setState({
        confirmDelete: true
      })
    }, "Delete market\u2026"), edit && s.confirmDelete && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: "12px"
      }
    }, "Delete ", /*#__PURE__*/React.createElement("b", null, this.props.market.name), "? Campaigns referencing it lose their market.", " ", /*#__PURE__*/React.createElement(Button, {
      size: "small",
      themeColor: "error",
      disabled: s.saving,
      onClick: this.remove
    }, "Delete"), " ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.setState({
        confirmDelete: false
      })
    }, "keep"))), /*#__PURE__*/React.createElement("span", {
      style: {
        display: "flex",
        gap: "8px"
      }
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: s.saving,
      onClick: this.props.onClose
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: s.saving,
      onClick: this.save
    }, s.saving ? "Saving…" : "Save")))));
  }
}